import { filterOperations, getOperationsFromProduct, getProductOperations } from '@/utils/product/operations';
import { getRetainFromVariations } from '@/utils/variations/retain';
import { shapeNewProductForAPI } from '@/utils/product/shapeForAPI';
import { shapeProductForApp } from '@/utils/product/shapeForApp';
import { convertProduct } from '@/utils/product/convert';
import { formHasErrors } from '@/utils/product/formHasErrors';
import { getSize } from '@/utils/iterable/getSize';

import { CHANNEL_ID, EBAY, ETSY, FAIRE, SHOPIFY } from '@/constants/channels';
import { ERROR, NEW, OPERATIONS, VALUE } from '@/constants/product';
import { RETAIN, VARIANTS } from '@/constants/variations';
import { EMPTY, VELA } from '@/constants';
import { FEATURE } from '@/constants/features';
import { STATUS } from '@/constants/listings';
import { FORM } from '@/constants/forms';
import { ALL } from '@/constants/profiles';

function getTemplateOperations({ channel, userFeatures }) {
  switch (channel) {
    case EBAY: {
      return [
        OPERATIONS[EBAY].PHOTOS.CHANGE_TO,
        OPERATIONS[EBAY].VIDEOS.CHANGE_TO,
        OPERATIONS[EBAY].TITLE,
        OPERATIONS[EBAY].DESCRIPTION,
        OPERATIONS[EBAY].ITEM_CATEGORY,
        OPERATIONS[EBAY].CONDITION_ID,
        OPERATIONS[EBAY].CONDITION_DESCRIPTION,
        OPERATIONS[EBAY].VARIATIONS,
        OPERATIONS[EBAY].PRICE,
        OPERATIONS[EBAY].QUANTITY,
        OPERATIONS[EBAY].SKU,
        OPERATIONS[EBAY].PAYMENT_POLICY,
        OPERATIONS[EBAY].RETURN_POLICY,
        OPERATIONS[EBAY].SHIPPING_POLICY,
        OPERATIONS[EBAY].COUNTRY,
        OPERATIONS[EBAY].LOCATION,
        OPERATIONS[EBAY].POSTAL_CODE,
        OPERATIONS[EBAY].IRREGULAR,
        OPERATIONS[EBAY].WEIGHT_MAJOR,
        OPERATIONS[EBAY].WEIGHT_MINOR,
        OPERATIONS[EBAY].DEPTH,
        OPERATIONS[EBAY].LENGTH,
        OPERATIONS[EBAY].WIDTH,
        OPERATIONS[EBAY].ITEM_SPECIFICS.UPDATE,
        OPERATIONS[EBAY].PRODUCT_DETAILS.UPDATE,
        OPERATIONS[EBAY].STORE_CATEGORY,
      ];
    }

    case ETSY: {
      const operations = [
        OPERATIONS[ETSY].PHOTOS.CHANGE_TO,
        OPERATIONS[ETSY].VIDEOS.CHANGE_TO,
        OPERATIONS[ETSY].TITLE,
        OPERATIONS[ETSY].DESCRIPTION,
        OPERATIONS[ETSY].TAGS.CHANGE_TO,
        OPERATIONS[ETSY].WHO_MADE,
        OPERATIONS[ETSY].IS_SUPPLY,
        OPERATIONS[ETSY].WHEN_MADE,
        OPERATIONS[ETSY].CATEGORY,
        OPERATIONS[ETSY].TAXONOMY_ATTRIBUTES,
        OPERATIONS[ETSY].SHOULD_AUTO_RENEW,
        OPERATIONS[ETSY].DIGITAL,
        OPERATIONS[ETSY].FILES.CHANGE_TO,
        OPERATIONS[ETSY].PRODUCTION_PARTNERS,
        OPERATIONS[ETSY].SECTION,
        OPERATIONS[ETSY].MATERIALS.CHANGE_TO,
        OPERATIONS[ETSY].VARIATIONS,
        OPERATIONS[ETSY].PRICE,
        OPERATIONS[ETSY].QUANTITY,
        OPERATIONS[ETSY].SKU,
        OPERATIONS[ETSY].PROCESSING_PROFILE,
        OPERATIONS[ETSY].SHIPPING_PROFILE,
        OPERATIONS[ETSY].WEIGHT,
        OPERATIONS[ETSY].LENGTH,
        OPERATIONS[ETSY].WIDTH,
        OPERATIONS[ETSY].HEIGHT,
        OPERATIONS[ETSY].RETURN_POLICY,
      ];

      if (!userFeatures.get(FEATURE.ETSY_PERSONALIZATION_QUESTIONS)) {
        operations.push(
          OPERATIONS[ETSY].PERSONALIZATION.IS_PERSONALIZABLE_SET,
          OPERATIONS[ETSY].PERSONALIZATION.IS_REQUIRED_SET,
          OPERATIONS[ETSY].PERSONALIZATION.CHAR_COUNT_MAX_SET,
          OPERATIONS[ETSY].PERSONALIZATION.INSTRUCTIONS_SET,
        );
      } else {
        operations.push(
          OPERATIONS[ETSY].PERSONALIZATION_QUESTIONS,
        );
      }

      return operations;
    }

    case FAIRE: {
      return [
        OPERATIONS[FAIRE].PHOTOS.CHANGE_TO,
        OPERATIONS[FAIRE].TITLE,
        OPERATIONS[FAIRE].DESCRIPTION,
        OPERATIONS[FAIRE].CATEGORY,
        OPERATIONS[FAIRE].VARIATIONS,
        OPERATIONS[FAIRE].PRICES,
        OPERATIONS[FAIRE].QUANTITY,
        OPERATIONS[FAIRE].RESTOCK_DATE,
        OPERATIONS[FAIRE].SKU,
        OPERATIONS[FAIRE].GTIN,
        OPERATIONS[FAIRE].CONTINUE_SELLING,
        OPERATIONS[FAIRE].PREORDER,
        OPERATIONS[FAIRE].START_DATE,
        OPERATIONS[FAIRE].END_DATE,
        OPERATIONS[FAIRE].DEADLINE_DATE,
        OPERATIONS[FAIRE].KEEP_ACTIVE,
        OPERATIONS[FAIRE].CASE_SIZE,
        OPERATIONS[FAIRE].MINIMUM_ORDER_QUANTITY,
        OPERATIONS[FAIRE].PER_STYLE_MINIMUM_ORDER_QUANTITY,
        OPERATIONS[FAIRE].TARIFF_CODE,
        OPERATIONS[FAIRE].MASS_UNIT,
        OPERATIONS[FAIRE].WEIGHT,
        OPERATIONS[FAIRE].DISTANCE_UNIT,
        OPERATIONS[FAIRE].HEIGHT,
        OPERATIONS[FAIRE].LENGTH,
        OPERATIONS[FAIRE].WIDTH,
      ];
    }

    case SHOPIFY: {
      return [
        OPERATIONS[SHOPIFY].PHOTOS.CHANGE_TO,
        OPERATIONS[SHOPIFY].VIDEOS.CHANGE_TO,
        OPERATIONS[SHOPIFY].TITLE,
        OPERATIONS[SHOPIFY].DESCRIPTION,
        OPERATIONS[SHOPIFY].TAGS.CHANGE_TO,
        OPERATIONS[SHOPIFY].PRODUCT_TYPE,
        OPERATIONS[SHOPIFY].VENDOR,
        OPERATIONS[SHOPIFY].COLLECTIONS,
        OPERATIONS[SHOPIFY].CATEGORY,
        OPERATIONS[SHOPIFY].PRICE,
        OPERATIONS[SHOPIFY].CAP,
        OPERATIONS[SHOPIFY].CPI,
        OPERATIONS[SHOPIFY].CHARGE_TAX,
        OPERATIONS[SHOPIFY].BARCODE,
        OPERATIONS[SHOPIFY].SKU,
        OPERATIONS[SHOPIFY].TRACK_QUANTITY,
        OPERATIONS[SHOPIFY].QUANTITY,
        OPERATIONS[SHOPIFY].CONTINUE_SELLING,
        OPERATIONS[SHOPIFY].COUNTRY_CODE,
        OPERATIONS[SHOPIFY].HS_CODE,
        OPERATIONS[SHOPIFY].PHYSICAL,
        OPERATIONS[SHOPIFY].WEIGHT,
        OPERATIONS[SHOPIFY].VARIATIONS,
        OPERATIONS[SHOPIFY].PAGE_TITLE,
        OPERATIONS[SHOPIFY].META_DESCRIPTION,
      ];
    }

    default: {
      return [];
    }
  }
}

export function setIsComplete({ sections, template, userFeatures }) {
  if (!template) return undefined;

  function reduceSections(valid, selected, section) {
    switch (section) {
      case ALL: {
        return valid;
      }

      default: {
        return selected
          ? valid && !formHasErrors({ product: template, type: section, userFeatures })
          : valid;
      }
    }
  }

  return template.set('isComplete', sections.reduce(reduceSections, true));
}

export function shapeTemplateForApp({ data, sections, shop, shopData, templateId, userFeatures }) {
  const template = shapeProductForApp({ asProfile: true, data, productId: templateId, shop, shopData, userFeatures });
  return setIsComplete({ sections, template, userFeatures });
}

export function getTemplateOperationsForProducts({ channel, profile, userFeatures }) {
  const sections = profile.get('sections');
  const template = profile.get('template');

  function filterDetailsOperations(operation) {
    switch (channel) {
      case EBAY: {
        switch (operation.get('type')) {
          case OPERATIONS[EBAY].CONDITION_ID:
          case OPERATIONS[EBAY].CONDITION_DESCRIPTION:
          case OPERATIONS[EBAY].ITEM_CATEGORY:
          case OPERATIONS[EBAY].ITEM_SPECIFICS.SET:
          case OPERATIONS[EBAY].ITEM_SPECIFICS.UPDATE:
          case OPERATIONS[EBAY].PRODUCT_DETAILS.SET:
          case OPERATIONS[EBAY].PRODUCT_DETAILS.UPDATE:
          case OPERATIONS[EBAY].STORE_CATEGORY: {
            return false;
          }

          default: {
            return true;
          }
        }
      }

      case ETSY: {
        switch (operation.get('type')) {
          case OPERATIONS[ETSY].CATEGORY:
          case OPERATIONS[ETSY].DIGITAL:
          case OPERATIONS[ETSY].IS_SUPPLY:
          case OPERATIONS[ETSY].MATERIALS.CHANGE_TO:
          case OPERATIONS[ETSY].PRODUCTION_PARTNERS:
          case OPERATIONS[ETSY].SECTION:
          case OPERATIONS[ETSY].SHOULD_AUTO_RENEW:
          case OPERATIONS[ETSY].TAXONOMY_ATTRIBUTES:
          case OPERATIONS[ETSY].WHEN_MADE:
          case OPERATIONS[ETSY].WHO_MADE: {
            return false;
          }

          default: {
            return true;
          }
        }
      }

      case FAIRE: {
        switch (operation.get('type')) {
          case OPERATIONS[FAIRE].CATEGORY: {
            return false;
          }

          default: {
            return true;
          }
        }
      }

      case SHOPIFY: {
        switch (operation.get('type')) {
          case OPERATIONS[SHOPIFY].CATEGORY:
          case OPERATIONS[SHOPIFY].COLLECTIONS:
          case OPERATIONS[SHOPIFY].PRODUCT_TYPE:
          case OPERATIONS[SHOPIFY].VENDOR: {
            return false;
          }

          default: {
            return true;
          }
        }
      }

      default: {
        return true;
      }
    }
  }

  function filterFilesOperations(operation) {
    switch (channel) {
      case ETSY: {
        switch (operation.get('type')) {
          case OPERATIONS[ETSY].FILES.ADD:
          case OPERATIONS[ETSY].FILES.CHANGE_TO:
          case OPERATIONS[ETSY].FILES.DELETE:
          case OPERATIONS[ETSY].FILES.REPLACE:
          case OPERATIONS[ETSY].FILES.SWAP: {
            return false;
          }

          default: {
            return true;
          }
        }
      }

      default: {
        return true;
      }
    }
  }

  function filterInventoryOperations(operation) {
    switch (channel) {
      case EBAY: {
        switch (operation.get('type')) {
          case OPERATIONS[EBAY].QUANTITY:
          case OPERATIONS[EBAY].SKU: {
            return false;
          }

          default: {
            return true;
          }
        }
      }

      case ETSY: {
        switch (operation.get('type')) {
          case OPERATIONS[ETSY].QUANTITY:
          case OPERATIONS[ETSY].SKU: {
            return false;
          }

          default: {
            return true;
          }
        }
      }

      case FAIRE: {
        switch (operation.get('type')) {
          case OPERATIONS[FAIRE].CONTINUE_SELLING:
          case OPERATIONS[FAIRE].DEADLINE_DATE:
          case OPERATIONS[FAIRE].END_DATE:
          case OPERATIONS[FAIRE].GTIN:
          case OPERATIONS[FAIRE].KEEP_ACTIVE:
          case OPERATIONS[FAIRE].PREORDER:
          case OPERATIONS[FAIRE].QUANTITY:
          case OPERATIONS[FAIRE].RESTOCK_DATE:
          case OPERATIONS[FAIRE].SKU:
          case OPERATIONS[FAIRE].START_DATE: {
            return false;
          }

          default: {
            return true;
          }
        }
      }

      case SHOPIFY: {
        switch (operation.get('type')) {
          case OPERATIONS[SHOPIFY].QUANTITY:
          case OPERATIONS[SHOPIFY].SKU:
          case OPERATIONS[SHOPIFY].BARCODE:
          case OPERATIONS[SHOPIFY].TRACK_QUANTITY:
          case OPERATIONS[SHOPIFY].CONTINUE_SELLING: {
            return false;
          }

          default: {
            return true;
          }
        }
      }

      default: {
        return true;
      }
    }
  }

  function filterPersonalizationOperations(operation) {
    switch (channel) {
      case ETSY: {
        switch (operation.get('type')) {
          case OPERATIONS[ETSY].PERSONALIZATION.CHAR_COUNT_MAX_SET:
          case OPERATIONS[ETSY].PERSONALIZATION.INSTRUCTIONS_SET:
          case OPERATIONS[ETSY].PERSONALIZATION.IS_PERSONALIZABLE_SET:
          case OPERATIONS[ETSY].PERSONALIZATION.IS_REQUIRED_SET:
          case OPERATIONS[ETSY].PERSONALIZATION_QUESTIONS: {
            return false;
          }

          default: {
            return true;
          }
        }
      }

      default: {
        return true;
      }
    }
  }

  function filterPricingOperations(operation) {
    switch (channel) {
      case EBAY: {
        switch (operation.get('type')) {
          case OPERATIONS[EBAY].PRICE:
          case OPERATIONS[EBAY].PAYMENT_POLICY: {
            return false;
          }

          default: {
            return true;
          }
        }
      }

      case ETSY: {
        switch (operation.get('type')) {
          case OPERATIONS[ETSY].PRICE: {
            return false;
          }

          default: {
            return true;
          }
        }
      }

      case FAIRE: {
        switch (operation.get('type')) {
          case OPERATIONS[FAIRE].PRICES: {
            return false;
          }

          default: {
            return true;
          }
        }
      }

      case SHOPIFY: {
        switch (operation.get('type')) {
          case OPERATIONS[SHOPIFY].PRICE:
          case OPERATIONS[SHOPIFY].CAP:
          case OPERATIONS[SHOPIFY].CPI:
          case OPERATIONS[SHOPIFY].CHARGE_TAX: {
            return false;
          }

          default: {
            return true;
          }
        }
      }

      default: {
        return true;
      }
    }
  }

  function filterSearchEngineOptimisatonOperations(operation) {
    switch (channel) {
      case SHOPIFY: {
        switch (operation.get('type')) {
          case OPERATIONS[SHOPIFY].META_DESCRIPTION:
          case OPERATIONS[SHOPIFY].PAGE_TITLE: {
            return false;
          }

          default: {
            return true;
          }
        }
      }

      default: {
        return true;
      }
    }
  }

  function filterShippingOperations(operation) {
    switch (channel) {
      case EBAY: {
        switch (operation.get('type')) {
          case OPERATIONS[EBAY].RETURN_POLICY:
          case OPERATIONS[EBAY].SHIPPING_POLICY:
          case OPERATIONS[EBAY].COUNTRY:
          case OPERATIONS[EBAY].LOCATION:
          case OPERATIONS[EBAY].POSTAL_CODE:
          case OPERATIONS[EBAY].IRREGULAR:
          case OPERATIONS[EBAY].WEIGHT_MAJOR:
          case OPERATIONS[EBAY].WEIGHT_MINOR:
          case OPERATIONS[EBAY].DEPTH:
          case OPERATIONS[EBAY].LENGTH:
          case OPERATIONS[EBAY].WIDTH: {
            return false;
          }

          default: {
            return true;
          }
        }
      }

      case ETSY: {
        switch (operation.get('type')) {
          case OPERATIONS[ETSY].WEIGHT:
          case OPERATIONS[ETSY].LENGTH:
          case OPERATIONS[ETSY].WIDTH:
          case OPERATIONS[ETSY].HEIGHT:
          case OPERATIONS[ETSY].RETURN_POLICY:
          case OPERATIONS[ETSY].SHIPPING_PROFILE:
          case OPERATIONS[ETSY].PROCESSING_PROFILE: {
            return false;
          }

          default: {
            return true;
          }
        }
      }

      case FAIRE: {
        switch (operation.get('type')) {
          case OPERATIONS[FAIRE].CASE_SIZE:
          case OPERATIONS[FAIRE].DISTANCE_UNIT:
          case OPERATIONS[FAIRE].HEIGHT:
          case OPERATIONS[FAIRE].LENGTH:
          case OPERATIONS[FAIRE].MASS_UNIT:
          case OPERATIONS[FAIRE].MINIMUM_ORDER_QUANTITY:
          case OPERATIONS[FAIRE].PER_STYLE_MINIMUM_ORDER_QUANTITY:
          case OPERATIONS[FAIRE].TARIFF_CODE:
          case OPERATIONS[FAIRE].WIDTH:
          case OPERATIONS[FAIRE].WEIGHT: {
            return false;
          }

          default: {
            return true;
          }
        }
      }

      case SHOPIFY: {
        switch (operation.get('type')) {
          case OPERATIONS[SHOPIFY].PHYSICAL:
          case OPERATIONS[SHOPIFY].COUNTRY_CODE:
          case OPERATIONS[SHOPIFY].HS_CODE:
          case OPERATIONS[SHOPIFY].WEIGHT: {
            return false;
          }

          default: {
            return true;
          }
        }
      }

      default: {
        return true;
      }
    }
  }

  function filterOperationsBySection({ section, selected, operations }) {
    switch (section) {
      case FORM.PHOTOS: {
        return selected
          ? operations
          : operations.filter(filterOperations(/^photos/, false));
      }

      case FORM.VIDEOS: {
        return selected
          ? operations
          : operations.filter(filterOperations(/^videos/, false));
      }

      case FORM.TITLE: {
        return selected && getSize(template.getIn([FORM.TITLE, VALUE])) > 0
          ? operations
          : operations.filter(filterOperations(OPERATIONS[channel].TITLE, false));
      }

      case FORM.DESCRIPTION: {
        return selected && getSize(template.getIn([FORM.DESCRIPTION, VALUE])) > 0
          ? operations
          : operations.filter(filterOperations(OPERATIONS[channel].DESCRIPTION, false));
      }

      case FORM.TAGS: {
        return selected
          ? operations
          : operations.filter(filterOperations(/^tags/, false));
      }

      case FORM.DETAILS: {
        return selected
          ? operations
          : operations.filter(filterOperations(filterDetailsOperations));
      }

      case FORM.PRICING: {
        switch (channel) {
          case EBAY:
          case ETSY: {
            return selected
              ? operations
              : operations.filter(filterPricingOperations);
          }

          case FAIRE:
          case SHOPIFY: {
            return (
              selected && (
                !sections.get(FORM.VARIATIONS) ||
                getSize(template.getIn([FORM.VARIATIONS, VARIANTS])) === 0
              )
            )
              ? operations
              : operations.filter(filterPricingOperations);
          }

          default: {
            return operations;
          }
        }
      }

      case FORM.SEO: {
        switch (channel) {
          case SHOPIFY: {
            return selected
              ? operations
              : operations.filter(filterOperations(filterSearchEngineOptimisatonOperations));
          }

          default: {
            return operations;
          }
        }
      }

      case FORM.INVENTORY: {
        switch (channel) {
          case EBAY:
          case ETSY: {
            return selected
              ? operations
              : operations.filter(filterOperations(filterInventoryOperations));
          }

          case FAIRE:
          case SHOPIFY: {
            return (
              selected && (
                !sections.get(FORM.VARIATIONS) ||
                getSize(template.getIn([FORM.VARIATIONS, VARIANTS])) === 0
              )
            )
              ? operations
              : operations.filter(filterInventoryOperations);
          }

          default: {
            return operations;
          }
        }
      }

      case FORM.VARIATIONS: {
        return selected
          ? operations
          : operations.filter(filterOperations(OPERATIONS[channel].VARIATIONS, false));
      }

      case FORM.PERSONALIZATION: {
        switch (channel) {
          case ETSY: {
            return selected
              ? operations
              : operations.filter(filterPersonalizationOperations);
          }

          default: {
            return operations;
          }
        }
      }

      case FORM.SHIPPING: {
        switch (channel) {
          case EBAY: {
            return selected
              ? operations
              : operations.filter(filterShippingOperations);
          }

          case ETSY: {
            return selected
              ? operations
              : operations.filter(filterShippingOperations);
          }

          case FAIRE:
          case SHOPIFY: {
            return (
              selected && (
                !sections.get(FORM.VARIATIONS) ||
                getSize(template.getIn([FORM.VARIATIONS, VARIANTS])) === 0
              )
            )
              ? operations
              : operations.filter(filterShippingOperations);
          }

          default: {
            return operations;
          }
        }
      }

      case FORM.FILES: {
        switch (channel) {
          case ETSY: {
            return selected
              ? operations
              : operations.filter(filterFilesOperations);
          }

          default: {
            return operations;
          }
        }
      }

      default: {
        return operations;
      }
    }
  }

  function reduceSections(operations, selected, section) {
    switch (section) {
      case ALL: {
        return operations;
      }

      default: {
        return filterOperationsBySection({ section, selected, operations });
      }
    }
  }

  const productOperations = getOperationsFromProduct({
    operationTypes: getTemplateOperations({ channel, userFeatures }),
    product: template.delete('status'),
  });

  return sections.get(ALL)
    ? productOperations
    : sections.reduce(reduceSections, productOperations);
}

export function convertProductToTemplate({ product, userFeatures }) {
  const channel = product.get('channel');
  const variations = product.get(FORM.VARIATIONS);
  const template = product
    .set('productId', NEW)
    .set('status', STATUS[VELA].TEMPLATE)
    .set(FORM.VARIATIONS, variations.set(RETAIN, getRetainFromVariations({ channel, variations })));

  const operations = getOperationsFromProduct({
    operationTypes: getProductOperations({ channel, userFeatures }),
    product: template,
  });

  return template.set('operations', operations);
}

export function shapeTemplateForAPI({ shop, shopData, template, userFeatures }) {
  let product = template;

  if (product.get('shopId') !== shop.get('id')) {
    product = convertProduct({
      from: product.get('channel'),
      to: shop.get('channel'),
      product: product.set('shopId', shop.get('id')).set(ERROR, EMPTY.MAP),
      shop,
      shopData,
      userFeatures,
    });

    product = product.set('operations',
      getOperationsFromProduct({
        operationTypes: getProductOperations({ channel: product.get('channel'), userFeatures }),
        product,
      })
    );
  }

  return {
    channel_id: CHANNEL_ID[product.get('channel')],
    operations: shapeNewProductForAPI({
      channel: product.get('channel'),
      product,
      shopId: shop.get('id'),
      status: STATUS[VELA].TEMPLATE,
      userFeatures,
    }),
    shopId: shop.get('id'),
    userDb: shop.get('db'),
  };
}
